import { __rest } from "tslib";
import React, { useEffect, useRef, useState } from 'react';
import { Editor } from './reactTinymce';
import { editorConfig, getMailEditorContentStyle } from '@/components/Layout/editorConfig';
import { apiHolder as api, apis, conf as config, inWindow } from 'api';
import HOCEditorGrammar from './HOCEditorGrammar';
import HOCEditorEmoji from './HOCEditorEmoji';
import HOCAiWriteMail from './HOCAiWriteMail';
import HOCEditorPreview from './HOCEditorPreview';
import HOCToolbarTip from './HOCToolbarTip';
import HOCCaptureScreen from './HOCCaptureScreen';
import HOCProductImg from './HOCProductImg';
import HOCUtils from './HOCUtils';
import HOCColor from './HOCColor';
import HOCFontFamily from './HOCFontFamily';
import HOCFontSize from './HOCFontSize';
import HOCLineHeight from './HOCLineHeight';
import HOCEditModal from './HOCEditModal';
const productAuthApi = api.api.requireLogicalApi(apis.productAuthApiImpl);
const performanceApi = api.api.requireLogicalApi(apis.performanceImpl);
const trackApi = api.api.requireLogicalApi(apis.dataTrackerApiImp);
const systemApi = api.api.getSystemApi();
const inEdm = systemApi.inEdm();
const isElectron = systemApi.isElectron();
const contextPath = config('contextPath');
const env = process.env.NODE_ENV;
const isProd = config('stage') === 'prod';
let siriusVersion = '';
if (inWindow() && typeof window.siriusVersion === 'string') {
    siriusVersion = window.siriusVersion.split('-')[0];
}
const tinymceENV = env !== 'production' || !isProd ? 'tinymce' : `tinymce-min`;
const eventsOut = (ed) => {
    const eventsMap = {
        MouseEvents: ['mousedown', 'click'],
    };
    Object.keys(eventsMap).forEach(key => {
        const events = eventsMap[key];
        events.forEach(eventName => {
            ed.on(eventName, () => {
                if (document) {
                    const event = document.createEvent(key);
                    event.initEvent(eventName, true, false);
                    document.body.dispatchEvent(event);
                }
            });
        });
    });
};
const fixMac26Bug = (body) => {
    try {
        const ua = navigator.userAgent;
        const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR/.test(ua);
        if (!isSafari)
            return false;
        const m = ua.match(/Version\/(\d+)(?:\.(\d+))?/);
        if (!m)
            return false;
        const major = parseInt(m[1], 10);
        const minor = parseInt(m[2] || '0', 10);
        if (major === 26 && minor === 2) {
            body.style.transform = 'translateZ(0)';
            const forceRepaint = () => {
                body.style.transform = 'translateZ(0)';
            };
            body.addEventListener('input', forceRepaint);
            body.addEventListener('compositionend', forceRepaint);
            return () => {
                try {
                    body === null || body === void 0 ? void 0 : body.removeEventListener('input', forceRepaint);
                    body === null || body === void 0 ? void 0 : body.removeEventListener('compositionend', forceRepaint);
                }
                catch (error) {
                    console.error('unmount fixMac26Bug error', error);
                }
            };
        }
    }
    catch (error) {
        console.warn('fixMac26Bug error', error);
    }
};
let LxEditor = React.forwardRef((_a, ref) => {
    var { init = {}, uniqueClass, grammarAction, insertEmojiAction, onEditCreated, aiWriteMailAction, AIWriteContentionShowAction, mouseoverToolbar, mouseoutToolbar, captureScreenAction, previewAction, productImgAction, utilsAction, colorAction, fontSizeAction, lineheightAction, fontfamilyAction, lazy } = _a, rest = __rest(_a, ["init", "uniqueClass", "grammarAction", "insertEmojiAction", "onEditCreated", "aiWriteMailAction", "AIWriteContentionShowAction", "mouseoverToolbar", "mouseoutToolbar", "captureScreenAction", "previewAction", "productImgAction", "utilsAction", "colorAction", "fontSizeAction", "lineheightAction", "fontfamilyAction", "lazy"]);
    const enableLazy = lazy !== false;
    const containerRef = useRef(null);
    const isIntersectionObserverSupported = typeof window !== 'undefined' && 'IntersectionObserver' in window;
    const [shouldLoad, setShouldLoad] = useState(!enableLazy || !isIntersectionObserverSupported);
    useEffect(() => {
        if (!enableLazy)
            return;
        if (shouldLoad)
            return;
        if (!isIntersectionObserverSupported) {
            setShouldLoad(true);
            return;
        }
        const el = containerRef.current;
        if (!el)
            return;
        let observer = null;
        try {
            observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setShouldLoad(true);
                        observer === null || observer === void 0 ? void 0 : observer.disconnect();
                    }
                });
            });
            observer.observe(el);
        }
        catch (e) {
            setShouldLoad(true);
        }
        return () => {
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
        };
    }, [enableLazy, isIntersectionObserverSupported]);
    const onUserInteract = () => {
        if (enableLazy && !shouldLoad)
            setShouldLoad(true);
    };
    const orgToolbar = init.toolbar ||
        `undo redo lxformatpainter removeformat $split fontselect fontsizeselect
  bold italic underline forecolor backcolor $split bullist numlist $split alignleftSplit
  dentSplit lxinputdirection $split lxTable lximg link lxemoji $split preview print code`;
    const [productVersion, setProductVersion] = useState('');
    const [toolbar, setToolbar] = useState(orgToolbar);
    useEffect(() => {
        productAuthApi.doGetProductVersion().then(res => {
            if (res.productVersionId !== 'sirius') {
                setToolbar(pre => {
                    const preStr = JSON.stringify(pre).replace('$split lxgrammar', '');
                    return JSON.parse(preStr);
                });
            }
            setProductVersion(res.productVersionId);
        });
    }, []);
    let unmountFixMac26Bug = null;
    const config = Object.assign(Object.assign(Object.assign({}, (shouldLoad ? { tinymceScriptSrc: contextPath + `/${tinymceENV}/tinymce.js?version=${siriusVersion}` } : {})), { init: Object.assign(Object.assign(Object.assign({}, editorConfig), init), { content_style: getMailEditorContentStyle(), inEdm, isWeb: !isElectron, grammarAction,
            previewAction,
            productImgAction,
            insertEmojiAction,
            aiWriteMailAction,
            mouseoverToolbar,
            mouseoutToolbar,
            siriusVersion,
            captureScreenAction,
            performanceApi,
            AIWriteContentionShowAction,
            utilsAction,
            colorAction,
            fontSizeAction,
            lineheightAction,
            fontfamilyAction, track: trackApi, init_instance_callback: (editor) => {
                eventsOut(editor);
                onEditCreated && onEditCreated(editor);
                init.init_instance_callback && init.init_instance_callback(editor);
                const edBody = editor.getBody();
                if (edBody) {
                    unmountFixMac26Bug = fixMac26Bug(edBody);
                }
            }, toolbar, automatic_uploads: false }) }), rest);
    useEffect(() => {
        return () => {
            typeof unmountFixMac26Bug === 'function' && unmountFixMac26Bug();
            unmountFixMac26Bug = null;
        };
    }, []);
    return (React.createElement("div", { className: uniqueClass, ref: containerRef, onMouseEnter: onUserInteract, onPointerDown: onUserInteract, onFocus: onUserInteract }, !!productVersion && shouldLoad && (React.createElement(Editor, Object.assign({}, config)))));
});
export default HOCEditorPreview(HOCEditorEmoji(HOCEditorGrammar(HOCToolbarTip(HOCAiWriteMail(HOCCaptureScreen(HOCProductImg(HOCUtils(HOCEditModal(LxEditor)))))))));
export const LXEditorForCRM = HOCEditorPreview(HOCEditorEmoji(HOCEditorGrammar(HOCToolbarTip(HOCCaptureScreen(HOCProductImg(HOCUtils(LxEditor)))))));
export const BaseWrite = HOCEditorPreview(HOCEditorEmoji(HOCEditorGrammar(HOCToolbarTip(HOCAiWriteMail(HOCCaptureScreen(HOCProductImg(HOCUtils(HOCColor(HOCFontFamily(HOCFontSize(HOCLineHeight(LxEditor))))))))))));
